import axiosInstance from '@/setup/axios/axiosInstance';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PurchaseOrderLinePayload {
  sort_order: number;
  item: string;
  uom: string;
  requested_qty: string;
  ordered_qty: string;
  unit_price: string;
  manufacturer?: string;
  source_request_line?: string;
  note?: string;
}

export interface PurchaseOrderPayload {
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'completed';
  purchase_type?: 'order' | 'reserve';
  company: string;
  supplier: string;
  warehouse: string;
  order?: string;
  material_request?: string;
  requested_by?: string;
  approved_by?: string;
  note?: string;
  lines: PurchaseOrderLinePayload[];
}

export interface PurchaseOrderResponse {
  id: string;
  status: string;
  company: string;
  supplier: string;
  warehouse: string;
  order?: string;
  material_request?: string;
  requested_by?: string;
  approved_by?: string;
  note?: string;
  lines: PurchaseOrderLinePayload[];
  [key: string]: any;
}

// ─── API calls ────────────────────────────────────────────────────────────────

/**
 * Create a single purchase order
 */
export async function createPurchaseOrder(payload: PurchaseOrderPayload): Promise<PurchaseOrderResponse> {
  const response = await axiosInstance.post<PurchaseOrderResponse>('/inventory/purchases/', payload);
  return response.data;
}

/**
 * Create multiple purchase orders for different suppliers
 * Each supplier gets one purchase order with their items
 */
export async function createPurchaseOrdersForSuppliers(
  orders: Array<{
    supplierName: string;
    supplierId: string;
    items: Array<{
      itemId: string;
      uomId: string;
      requestedQty: number;
      orderedQty: number;
      unitPrice: number;
      sourceRequestLineId?: string;
    }>;
    companyId: string;
    warehouseId: string;
    materialRequestId?: string;
    requestedById?: string;
    note?: string;
  }>,
): Promise<PurchaseOrderResponse[]> {
  const results: PurchaseOrderResponse[] = [];

  for (const order of orders) {
    const payload: PurchaseOrderPayload = {
      status: 'draft',
      purchase_type: 'order',
      company: order.companyId,
      supplier: order.supplierId,
      warehouse: order.warehouseId,
      material_request: order.materialRequestId,
      requested_by: order.requestedById,
      note: order.note,
      lines: order.items.map((item, index) => ({
        sort_order: index + 1,
        item: item.itemId,
        uom: item.uomId,
        requested_qty: String(item.requestedQty),
        ordered_qty: String(item.orderedQty),
        unit_price: String(item.unitPrice),
        source_request_line: item.sourceRequestLineId,
      })),
    };

    const result = await createPurchaseOrder(payload);
    results.push(result);
  }

  return results;
}
